/**
 * O roteador mínimo da H01: duas rotas, e o resto decidido pelo estado.
 *
 * ── Por que não há biblioteca de rota ainda ───────────────────────────────
 *
 * A H01 tem exatamente dois caminhos: a aplicação e o retorno do provedor. Uma
 * biblioteca de rota resolve navegação entre TELAS, e telas são H04 em diante —
 * escolher a biblioteca agora seria decidir sem o caso de uso que justifica a
 * decisão.
 *
 * O que existe aqui é o que o OIDC obriga: `/retorno` precisa ser um caminho
 * real, porque é ele que está no `redirectUris` do realm.
 *
 * O caso de uso chegou depois (#806): cada tela ganhou o seu endereço. A
 * escolha foi não trazer biblioteca: as telas moram na casca, e quem traduz
 * caminho ↔ tela e conversa com o histórico do navegador é `app/rotas/rotas.ts`.
 * Este roteador continua só separando a volta do login do resto.
 *
 * ── O estado da sessão é que decide o que aparece ─────────────────────────
 *
 * Não há rota "protegida" e rota "pública". Há um estado por vez, e cada um tem
 * uma tela. É o que impede o erro clássico de a casca renderizar por um quadro
 * antes de o guarda de rota redirecionar.
 */

import { useCallback, useState, type ReactNode } from 'react';
import { ProvedorDeDados } from '@/shared/api/provedor';
import { ProvedorDeSessao, useSessao } from '@/shared/sessao/contexto';
import { Casca } from './casca/casca';
import { Login } from '@/pages/acesso/login';
import { Retorno } from '@/pages/acesso/retorno';

function Roteador(): ReactNode {
  const { estado } = useSessao();
  const [caminho, definirCaminho] = useState(location.pathname);

  const { aoEstabelecerSessao } = useSessao();

  const aoConcluirRetorno = useCallback(
    ({ destino, revenda }: { destino: string; revenda: string | null }) => {
      // `replaceState` e não `assign`: recarregar a página levaria o código de
      // autorização na URL para o servidor e para o histórico, e ele é de uso
      // único justamente para não sobreviver a isso.
      history.replaceState(null, '', destino);
      definirCaminho(destino);

      /*
       * E avisa o provedor de sessão, que ainda não sabe de nada.
       *
       * Sem esta linha o token fica guardado e a aplicação continua `anonimo`:
       * a pessoa loga, volta, e cai na tela de entrada — sem erro em lugar
       * nenhum, porque não houve erro. O estado é que nunca foi relido.
       *
       * Quem já tinha cookie de SSO não passava por aqui: a renovação
       * silenciosa da primeira carga o recuperava, e ela chama a leitura da
       * sessão. Por isso o defeito só aparecia no PRIMEIRO login — em janela
       * anônima, ou para quem estava entrando pela primeira vez.
       */
      void aoEstabelecerSessao(revenda);
    },
    [aoEstabelecerSessao],
  );

  if (caminho === '/retorno') return <Retorno aoConcluir={aoConcluirRetorno} />;

  switch (estado.nome) {
    case 'indeterminado':
      // Sem spinner: a primeira carga tenta recuperar a sessão em silêncio e
      // normalmente leva menos de um segundo. Piscar um indicador nesse tempo
      // custa mais atenção do que economiza.
      return <div aria-busy="true" aria-live="polite" />;

    /*
     * A tela de entrada serve quatro estados, e a ESCOLHA da organização é um
     * deles.
     *
     * Ela não é a `Casca`: quem está escolhendo não tem papel, nem escopo, nem
     * `me`, e qualquer tela do produto leria `undefined`. E não é uma tela
     * própria porque, para quem entra, é o mesmo momento do login — mesmo
     * enquadramento, mesma marca, só o miolo troca.
     */
    case 'anonimo':
    case 'erro':
    case 'saiu':
    case 'escolhendo':
      // Uma tela, dez situações: quem decide qual é a ligação em `login.tsx`.
      return <Login />;

    case 'autenticado':
    case 'expirado':
      return <Casca />;
  }
}

export function Aplicacao(): ReactNode {
  // Sessão por fora, dados por dentro: o cache precisa do token para buscar, e
  // o fluxo SSE precisa saber quando há sessão para abrir. A ordem inversa
  // faria o provedor de dados tentar conectar antes de existir credencial.
  return (
    <ProvedorDeSessao>
      <ProvedorDeDados>
        <Roteador />
      </ProvedorDeDados>
    </ProvedorDeSessao>
  );
}
